import {
  createNavigationContainerRef,
  CommonActions,
  StackActions,
  DrawerActions,
} from '@react-navigation/native';
import type { RootStackParamList } from '@/navigations/types';

export const navigationRef = createNavigationContainerRef<RootStackParamList>();

export type HomeSessionReturnScreen = 'SelectPlayer' | 'OverviewSession';

/**
 * Replace navigation state so MutatePlayer / profile history is cleared. Home stack is
 * [Dashboard, screen] with active index 1 so Back returns to the dashboard.
 * Drawer + tab routes match `auth-stack` / `home-tab-navigator` registration order.
 */
export function resetRootAuthToHomeSessionScreen(screen: HomeSessionReturnScreen) {
  if (!navigationRef.isReady()) return;

  navigationRef.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [
        {
          name: 'Auth',
          state: {
            index: 0,
            routes: [
              {
                name: 'AuthDrawer',
                state: {
                  index: 0,
                  routes: [
                    {
                      name: 'HomeTabNavigator',
                      state: {
                        index: 0,
                        routes: [
                          {
                            name: 'Home',
                            state: {
                              index: 1,
                              routes: [{ name: 'Dashboard' }, { name: screen }],
                            },
                          },
                          { name: 'Player' },
                          { name: 'PlayerPathway' },
                        ],
                      },
                    },
                    { name: 'Test' },
                    { name: 'MyCoachProfile' },
                    { name: 'LoginCredentials' },
                    { name: 'Support' },
                  ],
                },
              },
            ],
          },
        },
      ],
    }),
  );
}

function isReady() {
  return navigationRef.isReady();
}

export const NavigationService = {
  navigate<T extends keyof RootStackParamList>(screen: T, params?: RootStackParamList[T]) {
    if (isReady()) {
      // @ts-expect-error NavigatorScreenParams variance
      navigationRef.navigate(screen, params);
    }
  },

  goBack() {
    if (isReady() && navigationRef.canGoBack()) {
      navigationRef.goBack();
    }
  },

  resetRoot<T extends keyof RootStackParamList>(screen: T, params?: RootStackParamList[T]) {
    if (isReady()) {
      navigationRef.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: screen, params }],
        }),
      );
    }
  },

  push(name: string, params?: object) {
    if (isReady()) {
      navigationRef.dispatch(StackActions.push(name, params));
    }
  },

  pop(count = 1) {
    if (isReady()) {
      navigationRef.dispatch(StackActions.pop(count));
    }
  },

  popToTop() {
    if (isReady()) {
      navigationRef.dispatch(StackActions.popToTop());
    }
  },

  replace(name: string, params?: object) {
    if (isReady()) {
      navigationRef.dispatch(StackActions.replace(name, params));
    }
  },

  openDrawer() {
    if (isReady()) {
      navigationRef.dispatch(DrawerActions.openDrawer());
    }
  },

  closeDrawer() {
    if (isReady()) {
      navigationRef.dispatch(DrawerActions.closeDrawer());
    }
  },

  toggleDrawer() {
    if (isReady()) {
      navigationRef.dispatch(DrawerActions.toggleDrawer());
    }
  },

  getCurrentRoute() {
    if (isReady()) {
      return navigationRef.getCurrentRoute();
    }
    return undefined;
  },

  canGoBack() {
    if (isReady()) {
      return navigationRef.canGoBack();
    }
    return false;
  },
};
